import {Component, inject, input, OnChanges, output, SimpleChanges, OnInit, ViewChild, ViewChildren, QueryList} from '@angular/core';
import {Activity, ACTIVITY_EVENT_OPTIONS} from "../../models/activity.model";
import {FormValidity} from "../detail-view-wrapper.component";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from "@angular/forms";
import {DataObject, Study} from "../../models/metadata";
import {
  RDS_DATE_FORMATS,
  RdsAutocompleteComponent,
  RdsButtonModule,
  RdsChipsModule,
  RdsDatepickerModule,
  RdsDividerModule,
  RdsEmptyStateModule,
  RdsFormFieldModule,
  RdsIconComponent,
  RdsPopoverModule,
  RdsProgressSpinnerComponent,
  RdsRadioButtonModule,
  RdsBadgeModule,
  RdsSwitchModule,
} from "@rds/angular-components";
import {ExternalProfile} from "../../models/external-profile.model";
import {ValidationErrorCollector} from "../../models/validation-error-collector.model";
import {DetailedViewObjectService} from "../../services/detailed-view-object.service";
import {DateFormatter} from "../../models/date-formatter.model";
import {
  DynamicActivityAutocompletionComponent
} from "./dynamic-activity-autocompletion/dynamic-activity-autocompletion.component";
import {ExpandableInputComponent} from "../expandable-input/expandable-input.component";
import {
  ExternalProfileDetailViewComponent
} from "./external-profile-detail-view/external-profile-detail-view.component";
import {FeedbackService} from "../../services/feedback.service";
import { UserService } from '../../services/user.service';
import { User } from '../../models/user.model';
import { StrategicPillar, StrategicPillarService } from '../../services/strategic-pillar.service';
import { AudienceService, UserAudienceResponse, UnifiedAudience } from '../../services/audience.service';
import { PillarsAudiencesSectionComponent } from '../../shared/pillars-audiences-section/pillars-audiences-section.component';

export type MinifiedProfile = {
  eID: string | undefined;
  text: string,
  isOld: boolean
}

export type VisibilityType = 'visible' | 'hidden'

type DetailedViewForm = FormGroup<{
  event: FormControl<string | null>;
  title: FormControl<string | null>;
  keyWords: FormArray<FormControl<string | null>>;
  link: FormControl<string | null>;
  dueDate: FormControl<Date | null>;
  externalParticipants: FormArray<FormControl<MinifiedProfile>>;
  externalLeads: FormArray<FormControl<MinifiedProfile>>;
  studies: FormArray<FormControl<Study>>;
  notes: FormControl<string | null>;
  isVisible: FormControl<boolean | null>;
}>

const MAX_PROFILE_RETRY = 3

@Component({
  selector: 'app-activity-detail-view',
  standalone: true,
  imports: [
    RdsButtonModule,
    RdsDividerModule,
    RdsEmptyStateModule,
    RdsFormFieldModule,
    RdsIconComponent,
    RdsPopoverModule,
    RdsProgressSpinnerComponent,
    RdsRadioButtonModule,
    ReactiveFormsModule,
    RdsDatepickerModule,
    RdsChipsModule,
    RdsBadgeModule,
    RdsAutocompleteComponent,
    RdsSwitchModule,
    DynamicActivityAutocompletionComponent,
    ExpandableInputComponent,
    ExternalProfileDetailViewComponent,
  PillarsAudiencesSectionComponent,
  ],
  providers: [
    { provide: RDS_DATE_FORMATS, useValue: DateFormatter.getDatePickerFormat() },
  ],
  templateUrl: './activity-detail-view.component.html',
  styleUrl: './activity-detail-view.component.scss'
})
export class ActivityDetailViewComponent implements OnChanges, OnInit {
  activity = input.required<Activity>()
  isLoadingChanges = input.required<boolean>()
  newActivity = output<{ data: Activity, valid: FormValidity, strategicPillarIds: number[], audiences: UnifiedAudience[] }>()
  deleteActivity = output<DataObject>()
  // Strategic pillar selector now encapsulated in unified section component
  @ViewChild(PillarsAudiencesSectionComponent) pillarsAudiencesSection!: PillarsAudiencesSectionComponent;
  
  private readonly formBuilder = inject(FormBuilder)
  form!: DetailedViewForm;
  externalProfiles: Array<ExternalProfile> = []
  externalLeadsEditViewStates: Array<boolean> = []
  externalParticipantsEditViewStates: Array<boolean> = []
  isLoadingProfiles = true
  protected readonly eventSuggestions = [...ACTIVITY_EVENT_OPTIONS].splice(0, 3);
  editorActionsVisible: VisibilityType = 'hidden'
  isViewer: boolean = true
  private strategicPillarService = inject(StrategicPillarService);
  pendingStrategicPillars: { pillarIds: number[], pillars: StrategicPillar[] } | null = null;
  pendingAudiences: { audienceIds: number[], audiences: UnifiedAudience[] } | null = null;

  constructor(private detailedViewObjectService: DetailedViewObjectService, 
    private feedbackService: FeedbackService,
    private userService: UserService,
    private audienceService: AudienceService,
  ) { }

  ngOnInit() {
    this.userService.getUserData().then((user: User) => {
      this.editorActionsVisible = user.isViewer ? 'hidden' : 'visible'
      this.isViewer = user.isViewer
      this.setupForm()
    }).catch((error: any) => {
      console.error('Error fetching user data', error);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activity']) {
      this.setupForm()
    }
  }

  private setupForm() {
    const a = this.activity()
    this.form = this.formBuilder.group({
      event: [{ value: a.event, disabled: this.isViewer }, Validators.required],
      title: [{ value: a.title, disabled: this.isViewer }, Validators.required],
      keyWords: this.formBuilder.array(a.keyWords.map(k => [{ value: k, disabled: this.isViewer }, this.keywordValidator])),
      link: [{ value: a.link, disabled: this.isViewer }],
      dueDate: [{ value: a.dueDate, disabled: this.isViewer }, Validators.required],
      externalParticipants: this.formBuilder.array([] as Array<FormControl<MinifiedProfile>>),
      externalLeads: this.formBuilder.array([] as Array<FormControl<MinifiedProfile>>),
      studies: this.formBuilder.array(a.studies.map(s => new FormControl({ value: s, disabled: this.isViewer }, { nonNullable: true })) as Array<FormControl<Study>>),
      notes: [{ value: a.notes, disabled: this.isViewer }],
      isVisible: [{ value: a.isVisible == false ? a.isVisible : true, disabled: this.isViewer }],
    })

    this.form.controls.studies.controls.forEach(studyControl => {
      studyControl.disable()
    })

    if(a.event !== "" && !this.eventSuggestions.includes(a.event)) {
      this.eventSuggestions.push(a.event)
    }
    const eIDs = new Set([...a.externalLeadIDs, ...a.externalParticipantIDs])

    if(eIDs.size > 0){
      this.detailedViewObjectService.getExternalProfilesWithID(...eIDs).subscribe(response => {
        this.externalProfiles = response.externalProfiles
        this.adaptsToExternalProfiles()
        this.isLoadingProfiles = false
        this.handleOutdatedProfiles()
      })
    }
    else{
      this.isLoadingProfiles = false;
    }
  }

  profileSelected(e: ExternalProfile | undefined, forLead: boolean, index: number){
    const profileListControl = this.form.controls[forLead ? "externalLeads" : "externalParticipants"]
    const otherListControl = this.form.controls[forLead ? "externalParticipants" : "externalLeads"]
    if(e) {
      // check if the person can be identified in our lists by the eID
      const existingIndex = profileListControl.value.findIndex(p => p.eID === e.eID)
      const otherIndex = otherListControl.value.findIndex(p => p.eID === e.eID)
      const profileIndex = this.externalProfiles.findIndex(p => p.eID === e.eID)

      // If the profile is not in the list of external profiles (holding the detailed information), add it.
      if(profileIndex == -1){
        this.externalProfiles.push(e)
      }
      // Otherwise update the detailed information.
      else{
        this.externalProfiles[profileIndex] = e
      }

      // If the profile is not already in the list of controls, add it.
      if(existingIndex == -1){
        profileListControl.controls[index].setValue(this.parseMinifiedProfile(e))
      }
      // Otherwise show a warning and update the existing profile.
      else{
        // Replace the old value with the new one.
        profileListControl.controls[existingIndex].setValue(this.parseMinifiedProfile(e))
        // Remove the autocompletion component manually
        this.removeProfile(index, forLead)

        this.feedbackService.emitCriticalWarning("Profile is already added to this list. Its values were updated instead.")
      }

      // If the same profile exists in the other list, update it as well.
      if(otherIndex != -1){
        otherListControl.controls[otherIndex].setValue(this.parseMinifiedProfile(e))
      }
    }
    else
      this.removeProfile(index, forLead)
  }

  getExcludedOptions(forLeads: boolean) {
    return forLeads? this.form.controls.externalLeads.value : this.form.controls.externalParticipants.value
  }

  createNewSuggestion (query: string) {
    return query;
  }

  adaptsToExternalProfiles(closeEditViews = true){
    this.form.controls.externalLeads.clear()
    for(let leadID of this.activity().externalLeadIDs){
      const lead = this.externalProfiles.find(p => p.eID === leadID)
      if(!lead){
        console.error(`External Lead with the ID ${leadID} does not exist`, this.activity())
        continue
      }
      this.form.controls.externalLeads.push(this.formBuilder.control({
          value: this.parseMinifiedProfile(lead),
          disabled: true
        }, {nonNullable: true})
      )
    }
    if(closeEditViews)
      this.externalLeadsEditViewStates = new Array(this.form.controls.externalLeads.length).fill(false)

    this.form.controls.externalParticipants.clear()
    for(let pID of this.activity().externalParticipantIDs){
      const p = this.externalProfiles.find(p => p.eID === pID)
      if(!p){
        console.error(`External participant with the ID ${pID} does not exist`, this.activity())
        continue
      }
      this.form.controls.externalParticipants.push(this.formBuilder.control({
          value: this.parseMinifiedProfile(p),
          disabled: true
        }, {nonNullable: true})
      )
    }
    if(closeEditViews)
      this.externalParticipantsEditViewStates = new Array(this.form.controls.externalParticipants.length).fill(false)
  }

  private handleOutdatedProfiles(emitUserFeedback = true, iteration = 0){
    const outdatedProfiles = this.externalProfiles.filter(p => p.isOld)
    if(outdatedProfiles.length > 0){
      if(iteration >= MAX_PROFILE_RETRY){
        this.feedbackService.emitCriticalWarning("Some profiles could not be updated. The data may be outdated.")
      }
      else{
        if(emitUserFeedback)
          this.feedbackService.emitInfo(`Some profiles are outdated. Expect them to change in a few seconds.`)
        setTimeout(() => this.reloadProfiles(outdatedProfiles, iteration), 3000)
      }
    }
    else if(iteration > 0){
      this.feedbackService.emitSuccess("All profiles are up to date again.")
    }
  }

  private reloadProfiles(outdatedProfiles: Array<ExternalProfile>, iteration: number){
    this.detailedViewObjectService.getExternalProfilesWithID(...outdatedProfiles.map(p => p.eID!)).subscribe(response => {
      this.externalProfiles = this.externalProfiles.map(p => {
        const updatedProfile = response.externalProfiles.find(rp => rp.eID === p.eID)
        return updatedProfile? updatedProfile : p
      })
      this.adaptsToExternalProfiles(false)
      this.handleOutdatedProfiles(false, ++iteration)
    })
  }

  parseMinifiedProfile(e: ExternalProfile): MinifiedProfile{
    return {
      eID: e.eID,
      text: this.formatExternalProfileName(e),
      isOld: e.isOld
    }
  }

  getExtendedProfile(eID: string): ExternalProfile {
    return this.externalProfiles.find(p => p.eID === eID)!
  }

  formatExternalProfileName(p: ExternalProfile) {
    return `${p.lastName}, ${p.firstName}`
  }

  toggleProfileDetailedViewState(state: boolean, index: number, forLeads: boolean) {
    if(forLeads)
      this.externalLeadsEditViewStates[index] = state
    else
      this.externalParticipantsEditViewStates[index] = state
  }

  addProfile(lead: boolean) {
    this.form.controls[lead ? "externalLeads" : "externalParticipants"].push(
      this.formBuilder.control({
        value: this.parseMinifiedProfile(new ExternalProfile()),
        disabled: true
      }, {nonNullable: true})
    )
  }

  removeProfile(i: number, lead: boolean){
    this.form.controls[lead? "externalLeads" : "externalParticipants"].removeAt(i)
    this[lead? 'externalLeadsEditViewStates' : 'externalParticipantsEditViewStates'].splice(i, 1)
  }

  getExcludedStudyOptions(){
    return this.form.controls.studies.value
  }

  studySelected(selection: Study | undefined, index: number){
    if(selection) {
      this.form.controls.studies.controls[index].setValue(selection)
      this.form.controls.studies.controls[index].disable()
    }
    else{
      this.removeStudy(index)
    }
  }

  addStudy() {
    this.form.controls.studies.push(
      this.formBuilder.control({
        value: {
          sID: undefined,
          studyNumber: "",
          studyName: ""
        } as Study,
        disabled: true
      }, {nonNullable: true})
    )
  }

  removeStudy(i: number, emitEvent = true){
    this.form.controls.studies.removeAt(i, {emitEvent})
  }

  keywordValidator(keywordControl: AbstractControl<string>) {
    const keyword = keywordControl.value
    if (!keyword || typeof keyword !== 'string' || keyword.trim() == "")
      return {emptyKeywordError: true} as ValidationErrors
    else
      return null
  }

  addKeyword() {
    this.form.controls.keyWords.push(this.formBuilder.control("", this.keywordValidator))
  }

  removeKeyword(i: number) {
    this.form.controls.keyWords.removeAt(i)
  }

  clearEmptyKeywords() {
    const indices: [string | null, number][] = this.form.controls.keyWords.value.map((x, i) => [x, i] as [string | null, number]).filter(k => k[0] === "")
    for (let [offset, idx] of indices.entries()) {
      this.form.controls.keyWords.removeAt(idx[1] - offset)
    }
  }

  onKeywordKeypress(e: KeyboardEvent) {
    if (e.code == "Enter") {
      e.preventDefault()
    }
  }

  onDelete(){
    this.deleteActivity.emit(this.activity() as DataObject)
  }

  onSubmit() {
    // Close any open dropdowns before submitting
    this.pillarsAudiencesSection?.closeAllDropdowns();
    
    const errors = new ValidationErrorCollector(this.form).errors;
    if (errors["emptyKeywordError"]) {
      this.clearEmptyKeywords();
    }
    const validity: FormValidity = this.form.valid ? 'valid' : 'required';
    const copy = Object.assign(new Activity(), JSON.parse(JSON.stringify(this.activity())));
    const ids = this.resolveSelectedProfileIDs();
    const post = Object.assign(copy, {
      ...this.form.value,
      externalParticipantIDs: ids.externalParticipantIDs,
      externalLeadIDs: ids.externalLeadIDs,
      studies: this.form.controls.studies.value,
      isVisible: this.form.controls.isVisible.value,
    } as Activity) as Activity;

    const existingPillarIDs = (this.activity().strategicPillars || []).map(p => p.id);
    const existingAudiences = (this.activity() as any).audiences || [];

    this.newActivity.emit({ 
      data: post, 
      valid: validity,
      strategicPillarIds: this.pendingStrategicPillars ? this.pendingStrategicPillars.pillarIds : existingPillarIDs,
      audiences: this.pendingAudiences ? this.pendingAudiences.audiences : existingAudiences
    });
  }

  resolveSelectedProfileIDs(){
    return {
      externalParticipantIDs: this.form.controls.externalParticipants.value.map(ep => ep.eID),
      externalLeadIDs: this.form.controls.externalLeads.value.map(ep => ep.eID)
    }
  }

  openLink(url: string = this.form.controls.link.value || '') { 
    const formattedUrl = url.startsWith('http') ? url : `https://${url}`;
    const newWindow = window.open(formattedUrl, '_blank', 'noopener,noreferrer');
    if (newWindow) {
      newWindow.opener = null;
    }
  }

  // Strategic Pillar event handlers
  onPillarChanged(selection: { pillarIds: number[], pillars: StrategicPillar[] }) {
    // Store the pending change for when user clicks Save
    this.pendingStrategicPillars = selection;
    console.log('Strategic pillar selection changed:', selection);
  }

  onAudienceSelectionChange(selection: { audienceIds: number[], audiences: UnifiedAudience[] }) {
    // Store the pending change for when user clicks Save
    this.pendingAudiences = selection;
    console.log('Audience selection changed:', selection);
  }
}
